import React, { useCallback, useMemo } from 'react';
import { useModal } from 'react-modal-hook';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { Avatar } from 'Components/Avatar/Avatar';
import ConfirmModal from 'Components/ConfirmModal';
import DropDownOptions from 'Components/DropDownOptions';
import UICheckbox from 'Components/UIComponents/UICheckbox';
import {
  useTeamMembersFreeze,
  useTeamMembersUnfreeze,
  useToAdminUpgrade,
  useToUserDowngrade,
} from 'Hooks/Team';
import { LtdTypes } from 'Interfaces/Billing';
import { TeamMember } from 'Interfaces/Team';
import { UserRoles } from 'Interfaces/User';
import { capitalize } from 'lodash-es';
import Toast from 'Services/Toast';
import { formatDate } from 'Utils/formatters';
import { selectTeamLimitAndLtdType } from 'Utils/selectors';

import FreezeTeammateModal from './FreezeTeammateModal';

import KeyIcon from 'Assets/images/icons/key-icon.svg';
import PauseIcon from 'Assets/images/icons/pause.svg';
import PlayIcon from 'Assets/images/icons/play.svg';

interface TeamMemberItemProps {
  teamMember: TeamMember;
  toggleSelect: (teamMemberId: TeamMember['id']) => void;
  className?: string;
  isSelected?: boolean;
  isSelectable?: boolean;
  isEditEnabled?: boolean;
  isChangingRoleEnabled?: boolean;
}

const TeamMemberItem = ({
  teamMember,
  className,
  isSelected = false,
  isSelectable,
  isChangingRoleEnabled = true,
  isEditEnabled = true,
  toggleSelect = () => {},
}: TeamMemberItemProps) => {
  const [upgradeToAdmin, isUpgradingToAdmin] = useToAdminUpgrade();
  const [downgradeToUser, isDowngradeToUser] = useToUserDowngrade();
  const [freezeTeamMember, isFreezeLoading] = useTeamMembersFreeze();
  const [unfreezeTeamMember, isUnfreezeLoading] = useTeamMembersUnfreeze();
  const [_, ltdType] = useSelector(selectTeamLimitAndLtdType);

  const handleToAdminUpgrade = useCallback(async () => {
    try {
      await upgradeToAdmin({ userId: teamMember.id });

      Toast.success('Team member successfully upgraded to admin');
    } catch (err) {
      Toast.handleErrors(err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToUserDowngrade = useCallback(async () => {
    try {
      await downgradeToUser({ userId: teamMember.id });

      Toast.success('Team member successfully downgraded to user');
    } catch (err) {
      Toast.handleErrors(err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFreezeTeamMember = useCallback(async (freezeUntil: string) => {
    try {
      await freezeTeamMember({ teamMembersIds: [teamMember.id], freezeUntil });

      Toast.success('Team member frozen successfully');
    } catch (err) {
      Toast.handleErrors(err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUnfreezeTeamMembers = useCallback(async () => {
    try {
      await unfreezeTeamMember({ teamMembersIds: [teamMember.id] });

      Toast.success('Team members unfrozen successfully');
    } catch (error) {
      Toast.handleErrors(error);
    }
  }, [unfreezeTeamMember, teamMember]);

  const [showFreezeModal, hideFreezeModal] = useModal(
    () => (
      <FreezeTeammateModal
        onClose={hideFreezeModal}
        onFreezeTeammate={handleFreezeTeamMember}
      />
    ),
    [handleFreezeTeamMember],
  );

  const [showUnfreezeModal, hideUnfreezeModal] = useModal(
    () => (
      <ConfirmModal
        onConfirm={() => {
          handleUnfreezeTeamMembers();
          hideUnfreezeModal();
        }}
        onClose={hideUnfreezeModal}
        onCancel={hideUnfreezeModal}
        confirmText={'Unfreeze'}
        isCancellable={true}
      >
        <div className="modal__header">
          <h4 className="modal__title">Do you want to unfreeze this team member?</h4>
        </div>
        <p className="modal__subTitle modal__subTitle--center">
          This action will result in the charge for additional team members for the
          remaining payment period.
        </p>
      </ConfirmModal>
    ),
    [handleUnfreezeTeamMembers],
  );

  const handleSelectionToggle = useCallback(() => {
    toggleSelect(teamMember.id);
  }, [toggleSelect, teamMember.id]);

  const isOptionProcessing =
    isUpgradingToAdmin || isDowngradeToUser || isFreezeLoading || isUnfreezeLoading;

  const options = useMemo(() => {
    return [
      {
        name: 'Upgrade to Admin',
        icon: KeyIcon,
        hidden:
          teamMember.role !== UserRoles.USER ||
          !isChangingRoleEnabled ||
          !!teamMember.frozenUntil,
        disabled: isOptionProcessing,
        onClick: handleToAdminUpgrade,
      },
      {
        name: 'Downgrade to User',
        icon: KeyIcon,
        hidden:
          teamMember.role !== UserRoles.ADMIN ||
          !isChangingRoleEnabled ||
          !!teamMember.frozenUntil,
        disabled: isOptionProcessing,
        onClick: handleToUserDowngrade,
      },
      {
        name: 'Freeze',
        icon: PauseIcon,
        iconClassName: 'documents__dropdownOption--blue-icon',
        hidden:
          teamMember.role === UserRoles.OWNER ||
          !isChangingRoleEnabled ||
          ltdType !== LtdTypes.NONE ||
          !!teamMember.frozenUntil,
        disabled: isOptionProcessing,
        onClick: showFreezeModal,
      },
      {
        name: 'Unfreeze',
        icon: PlayIcon,
        iconClassName: 'documents__dropdownOption--blue-icon',
        hidden:
          teamMember.role === UserRoles.OWNER ||
          !isChangingRoleEnabled ||
          ltdType !== LtdTypes.NONE ||
          !teamMember.frozenUntil,
        disabled: isOptionProcessing,
        onClick: showUnfreezeModal,
      },
    ];
  }, [
    teamMember.role,
    teamMember.frozenUntil,
    isChangingRoleEnabled,
    isOptionProcessing,
    handleToAdminUpgrade,
    handleToUserDowngrade,
    ltdType,
    showFreezeModal,
    showUnfreezeModal,
  ]);

  const hasActions = useMemo(() => !!options.filter(option => !option.hidden).length, [
    options,
  ]);

  return (
    <div className={classNames('table__row', 'table__dataRow', className)}>
      <div className="table__column table__column--check">
        {isSelectable && (
          <UICheckbox handleClick={handleSelectionToggle} check={isSelected} />
        )}
      </div>
      <div className="table__column table__column--text team__column-member">
        <div className="team__member">
          <Avatar
            name={teamMember.name}
            email={teamMember.email}
            avatarUrl={teamMember.avatarUrl}
          />
          <p className="team__member-name">{teamMember.name}</p>
        </div>
      </div>
      <div className="table__column table__column--team-email">{teamMember.email}</div>
      <div className="table__column table__column--team-role">
        {teamMember.frozenUntil
          ? `Frozen until ${formatDate(teamMember.frozenUntil)}`
          : 'Active'}
      </div>
      <div className="table__column table__column--team-role">
        {capitalize(teamMember.role)}
      </div>
      {(isEditEnabled || isChangingRoleEnabled) && (
        <div
          className={classNames(
            'team__column-actions',
            'table__column',
            'table__column--action',
            {
              'table__column--hidden': !hasActions,
            },
          )}
        >
          <DropDownOptions options={options} anchorClassName="table__container" />
        </div>
      )}
    </div>
  );
};

export default TeamMemberItem;
